import { addDays, endOfDay, startOfDay } from "date-fns";
import { useMemo } from "react";

import { useCan, useCustomerWaitingCarInShopNotification } from "components";
import { useSelectedLocation } from "components/DealersAndLocationsDropdown/hooks";
import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient, useTypedQuery } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { DayplannerKeys } from "modules/Dayplanner/queryKeys";
import { ApiInstance } from "utils/Api";
import { isBetween } from "utils/dateHelperFunctions";

import type { WebSocketMessageListener } from "components";
import type { Appointment, AppointmentNote, Intervention, User } from "models";
import type { DayplannerAppointment } from "modules/Dayplanner";

export const useDayplanner = () => {
  const { selectedLocation } = useSelectedLocation();
  const { notifyCustomerWaitingCarInShop } = useCustomerWaitingCarInShopNotification();
  const canListMechanics = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.DayplannerMechanics);

  const mechanicsQuery = useTypedQuery({
    queryKey: DayplannerKeys.mechanics,
    queryFn: async ({ baseUrl, endpoint }) => {
      if (!selectedLocation?.id) throw new Error("invalid location");
      return (await ApiInstance.post(endpoint, { dealer_location_id: selectedLocation.id }, baseUrl))?.data?.mechanics;
    },
    enabled: Boolean(selectedLocation?.id) && canListMechanics
  });

  const listeners = useMemo(() => {
    if (!selectedLocation?.id) return [];
    const appointmentsQueryKey = DayplannerKeys.appointments;

    const appointmentListeners: WebSocketMessageListener<DayplannerAppointment>[] = [
      {
        model: "Appointment",
        action: "upsert",
        filter: { dealer_location_id: selectedLocation.id },
        permanent: true,
        callback: message => {
          const appointments = typedQueryClient.getQueryData(appointmentsQueryKey);
          const appointmentIdx = appointments?.findIndex(appointment => appointment.id === message.id) ?? -1;
          const update = message.data;

          let updatedAppointments: Appointment[] | null = null;

          if (appointmentIdx < 0) {
            if (update.assigned_mechanic && appointments?.length) {
              updatedAppointments = appointments.map(appointment => {
                if (appointment.assigned_mechanic_order && appointment.assigned_mechanic === update.assigned_mechanic)
                  return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order + 1 };
                return appointment;
              });
              updatedAppointments = [...updatedAppointments, update];
            } else {
              const today = startOfDay(new Date());
              const tomorrow = endOfDay(addDays(today, 1));

              const shouldAddAppointment =
                (update.time_car_app && isBetween(update.time_car_app, today, tomorrow)) ||
                (update.last_car_ready && isBetween(update.last_car_ready, today, tomorrow)) ||
                update.is_pinned;

              if (shouldAddAppointment) updatedAppointments = [...(appointments ?? []), update];
            }
          } else if (appointments) {
            const appointment = appointments[appointmentIdx];

            const mechanicId = appointment.assigned_mechanic;
            const mechanicOrder = appointment.assigned_mechanic_order;
            const newMechanicId = update.assigned_mechanic;
            const newMechanicOrder = update.assigned_mechanic_order;

            if (mechanicId === newMechanicId && mechanicOrder === newMechanicOrder) {
              updatedAppointments = appointments.with(appointmentIdx, { ...appointment, ...update });
            } else {
              updatedAppointments = appointments.map(appointment => {
                if (appointment.id === message.id) return { ...appointment, ...update };

                if (mechanicId === newMechanicId && mechanicId && mechanicOrder && newMechanicOrder) {
                  if (newMechanicOrder > mechanicOrder) {
                    if (
                      appointment.assigned_mechanic === mechanicId &&
                      appointment.assigned_mechanic_order &&
                      appointment.assigned_mechanic_order > mechanicOrder &&
                      appointment.assigned_mechanic_order <= newMechanicOrder
                    ) {
                      return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order - 1 };
                    }
                  } else if (newMechanicOrder < mechanicOrder) {
                    if (
                      appointment.assigned_mechanic === mechanicId &&
                      appointment.assigned_mechanic_order &&
                      appointment.assigned_mechanic_order >= newMechanicOrder &&
                      appointment.assigned_mechanic_order < mechanicOrder
                    ) {
                      return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order + 1 };
                    }
                  }
                }

                if (mechanicId !== newMechanicId && mechanicId && newMechanicId && mechanicOrder && newMechanicOrder) {
                  if (appointment.assigned_mechanic === mechanicId && appointment.assigned_mechanic_order && appointment.assigned_mechanic_order > mechanicOrder) {
                    return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order - 1 };
                  }

                  if (appointment.assigned_mechanic === newMechanicId && appointment.assigned_mechanic_order && appointment.assigned_mechanic_order >= newMechanicOrder) {
                    return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order + 1 };
                  }
                }

                if (!mechanicId && newMechanicId && newMechanicOrder) {
                  if (appointment.assigned_mechanic === newMechanicId && appointment.assigned_mechanic_order && appointment.assigned_mechanic_order >= newMechanicOrder) {
                    return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order + 1 };
                  }
                }

                if (mechanicId && !newMechanicId && mechanicOrder) {
                  if (appointment.assigned_mechanic === mechanicId && appointment.assigned_mechanic_order && appointment.assigned_mechanic_order > mechanicOrder) {
                    return { ...appointment, assigned_mechanic_order: appointment.assigned_mechanic_order - 1 };
                  }
                }

                return appointment;
              });
            }
          }

          if (!updatedAppointments) return;

          if (appointments) notifyCustomerWaitingCarInShop({ oldAppointment: appointments[appointmentIdx], newAppointment: update, userId: message.user_id });
          typedQueryClient.setQueryData(appointmentsQueryKey, updatedAppointments);
        }
      }
    ];

    const appointmentNoteListeners: WebSocketMessageListener<AppointmentNote>[] = [
      {
        model: "AppointmentNote",
        action: "upsert",
        permanent: true,
        callback: message => {
          const appointments = typedQueryClient.getQueryData(appointmentsQueryKey);
          if (!appointments?.length) return;

          const appointmentNote = message.data;
          const appointmentIdx = appointments.findIndex(appointment => appointment.id === appointmentNote.appointment_id);
          if (appointmentIdx < 0) return;

          const appointment = appointments[appointmentIdx];
          const notes = appointment.notes ?? [];
          const appointmentNoteIdx = notes.findIndex(n => n.id === appointmentNote.id);

          let updatedNotes = [...notes, appointmentNote];
          if (appointmentNoteIdx > -1) updatedNotes = notes.with(appointmentNoteIdx, { ...notes[appointmentNoteIdx], ...appointmentNote });

          typedQueryClient.setQueryData(appointmentsQueryKey, appointments.with(appointmentIdx, { ...appointment, notes: updatedNotes }));
        }
      },
      {
        model: "AppointmentNote",
        action: "delete",
        permanent: true,
        callback: message => {
          const appointments = typedQueryClient.getQueryData(appointmentsQueryKey);
          if (!appointments?.length) return;

          const appointmentNote = message.data;
          const appointmentIdx = appointments.findIndex(appointment => appointment.id === appointmentNote.appointment_id);
          if (appointmentIdx < 0) return;

          const appointment = appointments[appointmentIdx];
          if (!appointment.notes?.length) return;

          const appointmentNoteIdx = appointment.notes.findIndex(n => n.id === appointmentNote.id);
          if (appointmentNoteIdx < 0) return;

          const updatedAppointmentNotes = appointment.notes.toSpliced(appointmentNoteIdx, 1);

          typedQueryClient.setQueryData(appointmentsQueryKey, appointments.with(appointmentIdx, { ...appointment, notes: updatedAppointmentNotes }));
        }
      }
    ];

    const interventionListeners: WebSocketMessageListener<Intervention>[] = [
      {
        model: "Intervention",
        action: "upsert",
        permanent: true,
        callback: message => {
          const appointments = typedQueryClient.getQueryData(appointmentsQueryKey);
          if (!appointments?.length) return;

          const intervention = message.data;
          const appointmentIdx = appointments.findIndex(appointment => appointment.id === intervention.appointment_id);
          if (appointmentIdx < 0) return;

          const appointment = appointments[appointmentIdx];
          const interventionIdx = appointment.interventions?.findIndex(i => i.id === intervention.id) ?? -1;

          let updatedInterventions = [...(appointment.interventions ?? []), intervention];
          if (interventionIdx > -1 && appointment.interventions?.length)
            updatedInterventions = appointment.interventions.with(interventionIdx, { ...appointment.interventions[interventionIdx], ...intervention });

          typedQueryClient.setQueryData(appointmentsQueryKey, appointments.with(appointmentIdx, { ...appointment, interventions: updatedInterventions }));
        }
      },
      {
        model: "Intervention",
        action: "delete",
        permanent: true,
        callback: message => {
          const intervention = message.data;
          const appointments = typedQueryClient.getQueryData(appointmentsQueryKey);
          if (!appointments?.length) return;

          const appointmentIdx = appointments.findIndex(appointment => appointment.id === intervention.appointment_id);
          if (appointmentIdx < 0) return;

          const appointment = appointments[appointmentIdx];
          if (!appointment.interventions?.length) return;

          typedQueryClient.setQueryData(
            appointmentsQueryKey,
            appointments.with(appointmentIdx, { ...appointment, interventions: appointment.interventions.filter(n => n.id !== intervention.id) })
          );
        }
      }
    ];

    const userListeners: WebSocketMessageListener<User>[] = [
      {
        model: "User",
        action: "upsert",
        filter: { dealer_location_id: selectedLocation.id },
        callback: message => {
          const mechanics = typedQueryClient.getQueryData(DayplannerKeys.mechanics);
          if (!mechanics?.length) return;

          const user = message.data;
          const mechanicIdx = mechanics.findIndex(mechanic => mechanic.id === user.id);
          if (mechanicIdx < 0) return;

          typedQueryClient.setQueryData(DayplannerKeys.mechanics, mechanics.with(mechanicIdx, { ...mechanics[mechanicIdx], ...user }));
        }
      }
    ];

    return [...appointmentListeners, ...appointmentNoteListeners, ...interventionListeners, ...userListeners];
  }, [typedQueryClient, selectedLocation?.id]);

  const realTimeAppointmentsQuery = useRealTimeTypedQuery({
    queryKey: DayplannerKeys.appointments,
    queryFn: async ({ baseUrl, endpoint }) => {
      if (!selectedLocation?.id) throw new Error("invalid location");
      return (await ApiInstance.post(endpoint, { dealer_location_id: selectedLocation.id }, baseUrl))?.data?.appointments;
    },
    enabled: Boolean(selectedLocation?.id),
    listeners
  });

  const loading = mechanicsQuery.isFetching || realTimeAppointmentsQuery.isFetching;
  const error = mechanicsQuery.error || realTimeAppointmentsQuery.error;

  return {
    appointments: realTimeAppointmentsQuery.data,
    mechanics: mechanicsQuery.data,
    refetch: realTimeAppointmentsQuery.refetch,
    refetchMechanics: mechanicsQuery.refetch,
    loading,
    error
  };
};
